import { SETTING_LANDED_ATTRIBUTE, SETTING_LANDED_MS } from './setting-anchor-classes';
import { SETTING_ANCHORS, settingAnchorHref } from './setting-anchors';

import type { SettingKey } from './setting-anchors';

/**
 * Opening a setting from somewhere else in the app: navigate to its page and
 * tab, then put the caret on the exact card (or control) that was asked for and
 * mark it so the eye finds it.
 *
 * Moved out of `useSettingCommands.ts` because it is no longer the ⌘K palette's
 * private helper — the top-bar search field performs the same jump, and one of
 * the two surfaces landing differently from the other is the drift this split
 * exists to prevent.
 *
 * ## Why this is a poll and not a single call
 *
 * A jump crosses a lazy route chunk, a role read and the target card's own
 * query, so the element does not exist on the next tick. Polling a bounded
 * number of frames is the honest shape: it lands as soon as the card mounts, and
 * gives up quietly rather than hanging or throwing if the card turns out never
 * to render.
 *
 * ## The three ways a jump used to miss
 *
 * 1. **The closing dialog took the focus back.** ⌘K runs the jump while its own
 *    Radix dialog is closing, and a closing dialog restores focus to whatever
 *    opened it — AFTER our synchronous `focus()` call. So the first attempt is
 *    deferred one macrotask, which puts it behind that restore.
 * 2. **"Found" was read as "landed".** The old loop stopped the moment
 *    `getElementById` returned an element, whether or not the focus actually
 *    took. Focus can be refused (a `display: none` ancestor) or stolen a tick
 *    later, and the jump then reported success having moved nothing. The loop
 *    now keeps going until `document.activeElement` really is the target.
 * 3. **It landed inside a hidden tab.** `/settings/workspace` force-mounts all
 *    three tab panels and hides the inactive ones with the `hidden` attribute
 *    (so an unsaved edit survives a tab switch). `getElementById` finds a card
 *    in there happily; focusing it moves the caret into a panel nobody can see.
 *    An element under `[hidden]` is treated as not-there-yet, so the poll waits
 *    for the tab the URL asked for to become the visible one.
 */

/** How often to re-check for the target, and for how long in total. */
const FOCUS_POLL_MS = 60;
const FOCUS_TIMEOUT_MS = 4_000;

export interface FocusSettingAnchorOptions {
  /** Injectable clock, so the timeout is testable without a real wait. */
  readonly now?: () => number;
  /** How long to keep looking before giving up quietly. */
  readonly deadlineMs?: number;
  /** How long the landing mark stays before clearing itself. */
  readonly landedMs?: number;
}

/**
 * The DOM id of a setting's card, or of one control inside it.
 *
 * A card that mixes unrelated options (Account preferences, Limits) registers
 * each option as its own control, and a control's id is derived from its card's
 * so the two can never be addressed independently of each other.
 */
export function settingDomId(key: SettingKey, controlId?: string): string {
  const { id } = SETTING_ANCHORS[key];
  return controlId === undefined ? id : `${id}--${controlId}`;
}

/**
 * The element to land on, or `null` while it is still not reachable.
 *
 * Not-reachable covers both "not in the document yet" and "in the document but
 * inside a hidden tab panel" — see reason 3 in the module comment.
 */
function findLandingTarget(id: string): HTMLElement | null {
  const target = document.getElementById(id);
  if (target === null) return null;
  // `closest` also matches the element itself, which is right: a card that is
  // itself `hidden` is no more focusable than one under a hidden ancestor.
  if (target.closest('[hidden]') !== null) return null;
  return target;
}

/**
 * Mark the landed element for a moment, then clear the mark.
 *
 * Clears on blur as well as on the timer, because the mark's whole job is "the
 * thing you just asked for is here" — once the reader has moved on it is noise,
 * and a stale outline reads as a second focus ring.
 */
function markLanded(target: HTMLElement, landedMs: number): void {
  target.setAttribute(SETTING_LANDED_ATTRIBUTE, 'true');
  const clear = (): void => {
    // `timer` is initialised on the next line, and `clear` cannot run before
    // that: it is only ever reached from the timer itself or from a later blur.
    clearTimeout(timer);
    target.removeAttribute(SETTING_LANDED_ATTRIBUTE);
    target.removeEventListener('blur', clear);
  };
  const timer = setTimeout(clear, landedMs);
  target.addEventListener('blur', clear, { once: true });
}

/**
 * Move focus to a setting's card — or to one control inside it — once it appears,
 * and mark it briefly.
 *
 * Focus, not just scroll: after a keyboard-driven jump the caret has to land ON
 * the thing the user asked for, or the next Tab continues from wherever the
 * search box was. Anchored cards are `tabIndex={-1}` for exactly this — focusable
 * by script, never inserted into the tab order.
 *
 * `focus()` scrolls the element into view as a side effect, and the card's own
 * scroll margin keeps its heading clear of the sticky shell chrome. The `#id` in
 * the URL does NOT do that on a client-side navigation — it is there so the link
 * can be copied and pasted (see `settingAnchorHref`).
 */
export function focusSettingAnchor(id: string, options: FocusSettingAnchorOptions = {}): void {
  const {
    now = () => Date.now(),
    deadlineMs = FOCUS_TIMEOUT_MS,
    landedMs = SETTING_LANDED_MS,
  } = options;
  const started = now();

  const attempt = (): void => {
    // The poll can outlive the document it is polling — a test environment torn
    // down mid-retry, or a bundle evaluated where there is no DOM at all. There
    // is nothing to focus in either case, so stop; continuing threw a
    // `ReferenceError: document is not defined` out of a bare timer, where no
    // caller could catch it (it failed the whole vitest run despite every test
    // passing).
    if (typeof document === 'undefined') return;
    const target = findLandingTarget(id);
    if (target !== null) {
      target.focus();
      // "Found" is not "landed" — see reason 2 in the module comment.
      if (document.activeElement === target) {
        markLanded(target, landedMs);
        return;
      }
    }
    if (now() - started >= deadlineMs) return;
    setTimeout(attempt, FOCUS_POLL_MS);
  };

  // Deferred one macrotask, behind the closing dialog's focus restore — see
  // reason 1 in the module comment.
  setTimeout(attempt, 0);
}

/**
 * What `useNavigate()` hands back — typed structurally here so this module stays
 * React-free. The return is `unknown` because React Router's signature returns
 * `void` or a promise depending on the overload, and neither is something a
 * caller reads.
 */
type NavigateTo = (to: string) => unknown;

/**
 * Open a setting: navigate to its page and tab, then land on it.
 *
 * The one function both search surfaces call, so the ⌘K palette and the top-bar
 * field can never disagree about where a result goes or what it does on arrival.
 */
export function jumpToSetting(navigate: NavigateTo, key: SettingKey, controlId?: string): void {
  void navigate(settingAnchorHref(key, controlId));
  focusSettingAnchor(settingDomId(key, controlId));
}
